<script lang="ts" setup>
import { computed, unref } from 'vue'
import { MaybeRef } from '@vueuse/core'
import { ITEMS_PER_PAGE_OPTIONS } from './useJobListPagination'

interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: MaybeRef<number>
  isFetching: boolean
  isCountFetching: boolean
  hasNextPage?: boolean | null
  visibleCount: number
  updatePage: (page: number) => void
  updateItemsPerPage: (itemsPerPage: number) => void
}

const props = withDefaults(defineProps<PaginationProps>(), {
  hasNextPage: false,
})

const selectedItemsPerPage = computed({
  get: () => unref(props.itemsPerPage),
  set: (value: number) => props.updateItemsPerPage(value),
})

const previousButtonEnabled = computed<boolean>(() => {
  return props.currentPage > 1 && !props.isFetching && !props.isCountFetching
})

const nextButtonEnabled = computed<boolean>(() => {
  return props.hasNextPage === true && !props.isFetching && !props.isCountFetching
})

const currentRange = computed(() => {
  const itemsPerPage = unref(props.itemsPerPage)
  const start = (props.currentPage - 1) * itemsPerPage + 1
  let end = Math.min(start + props.visibleCount - 1, props.totalItems)
  if (end < start) {
    end = start
  }
  return `${start}-${end}`
})

const totalDisplay = computed(() => {
  const total = props.totalItems >= 10000 ? `${props.totalItems}+` : props.totalItems
  return ` of ${total}`
})

function goToPreviousPage() {
  if (props.currentPage > 1) props.updatePage(props.currentPage - 1)
}

function goToNextPage() {
  if (props.hasNextPage === true) props.updatePage(props.currentPage + 1)
}
</script>

<template>
  <div class="d-flex align-center pagination-container">
    <div class="d-flex align-center flex-grow-1">
      <vs-text class="ml-4 mr-4" tag="span">Rows per page:</vs-text>
      <v-select
        v-model="selectedItemsPerPage"
        :items="ITEMS_PER_PAGE_OPTIONS"
        class="items-per-page-select pl-2"
        hide-details
        dense
        flat
        solo
      />
    </div>

    <div class="ml-8 d-flex align-center">
      <v-btn
        :disabled="!previousButtonEnabled"
        class="navigation-btn"
        icon
        @click="goToPreviousPage"
      >
        <v-icon size="21">mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn :disabled="!nextButtonEnabled" class="navigation-btn mr-7" icon @click="goToNextPage">
        <v-icon size="21">mdi-chevron-right</v-icon>
      </v-btn>

      <vs-text tag="span" :class="{ barbershop: isFetching }">
        {{ currentRange }}
      </vs-text>
      <vs-text tag="span" :class="{ barbershop: isCountFetching }">
        {{ totalDisplay }}
      </vs-text>
    </div>
  </div>
</template>

<style scoped>
.current-range {
  min-width: 200px;
  display: inline-block;
  text-align: center;
}

.items-per-page-select {
  max-width: 5.9rem !important;
  border-radius: 50px;
  border: 1px solid var(--color-grey--lighter);
  min-height: 2.5rem !important;
}

.items-per-page-select:hover {
  border-color: var(--color-blue);
}

.items-per-page-select:focus {
  border-color: var(--color-blue);
}

.barbershop {
  position: relative;
  display: inline-block;
  padding: 2px 8px;
  margin: -9px -8px;
  border-radius: 32px;
  overflow: hidden;
  background-image: linear-gradient(
    135deg,
    rgba(0, 0, 0, 10%) 0%,
    rgba(0, 0, 0, 10%) 25%,
    transparent 25%,
    transparent 50%,
    rgba(0, 0, 0, 10%) 50%,
    rgba(0, 0, 0, 10%) 75%,
    transparent 75%,
    transparent 100%
  );
  background-size: 50px 50px;
  background-repeat: repeat;
  pointer-events: none;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-name: animateStripes;
  animation-timing-function: linear;
}

@keyframes animateStripes {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
}
</style>
